// src/pages/PrivacyPolicy.js
import React from 'react';
import { motion } from 'framer-motion';
import SEO from "../components/SEO"; // Import SEO component

function PrivacyPolicy() {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1 }}
      className="container"
      style={{ padding: '2rem', maxWidth: '1000px', margin: '0 auto', lineHeight: '1.6', fontSize: '1.1rem' }}
    >
      <SEO /> {/* Auto-injects structured data */}
      {/* Title */}
      <h1 style={{ textAlign: 'center', marginBottom: '2rem', fontSize: '2.5rem' }}>
        Privacy Policy
      </h1>

      {/* Introduction */}
      <section style={{ marginBottom: '2rem' }}>
        <h2 style={{ fontSize: '2rem', marginBottom: '1rem' }}>Introduction</h2>
        <p>
          Welcome to WaveTech Solutions ("we", "us", or "our"). This Privacy Policy explains how we collect,
          use, disclose, and safeguard your information when you visit our website (the "Site") or use our
          services, including our Shopify applications such as Wave Notify. Your privacy is very important to
          us, and we are committed to protecting your personal information.
        </p>
        <p>
          By accessing or using our services, you agree to the collection and use of information in accordance
          with this policy. If you do not agree with our policies, please do not access or use our Site.
        </p>
      </section>

      {/* Information We Collect */}
      <section style={{ marginBottom: '2rem' }}>
        <h2 style={{ fontSize: '2rem', marginBottom: '1rem' }}>Information We Collect</h2>
        <p>
          We may collect various types of information in connection with your use of our Site and services,
          including our Shopify applications (such as <strong>Wave Notify</strong> and <strong>Wave Sync</strong>).
          This includes:
        </p>
        <ul style={{ marginLeft: '1.5rem', marginBottom: '1rem' }}>
          <li>
            <strong>Personal Identification Information:</strong> such as your name, email address, phone
            number, and mailing address when you register for an account, use our apps, or contact us.
          </li>
          <li>
            <strong>Shop & Account Information:</strong> basic shop information from Shopify (for example,
            shop domain, plan, locale, and install date) when you install or use our apps.
          </li>
          <li>
            <strong>Usage Data:</strong> details of your visits to our Site, including traffic data, location,
            logs, and other communication data.
          </li>
          <li>
            <strong>Cookies and Tracking Technologies:</strong> information collected via cookies, web beacons, or
            similar technologies to enhance your experience.
          </li>
          <li>
            <strong>Transaction & Subscription Data:</strong> if you use our services to purchase products or
            subscribe to our Shopify apps, we may collect data related to the transaction and your app usage.
          </li>
          <li>
            <strong>Technical Data:</strong> such as your IP address, browser type and version, time zone setting,
            and other system settings.
          </li>
        </ul>
        <p>
          We collect this information to provide, maintain, and improve our services and apps, as well as to communicate
          with you and comply with legal obligations.
        </p>
      </section>

      {/* Shopify Apps Overview */}
      <section style={{ marginBottom: '2rem' }}>
        <h2 style={{ fontSize: '2rem', marginBottom: '1rem' }}>Shopify Apps We Operate</h2>
        <p>
          WaveTech Solutions develops and maintains Shopify applications that run inside the merchant&apos;s Shopify
          admin. Below is a high-level privacy overview for our current apps. These summaries are provided for clarity
          and are intended to be read together with the rest of this Privacy Policy and Shopify&apos;s own terms and
          policies.
        </p>

        {/* Wave Notify */}
        <h3 style={{ fontSize: '1.5rem', margin: '1.5rem 0 0.75rem' }}>Wave Notify – Privacy Overview</h3>
        <p>
          <strong>Wave Notify</strong> is a notifications app that runs inside the merchant&apos;s Shopify admin. It
          accesses store data through Shopify&apos;s APIs, including basic shop information (such as shop domain, plan,
          and locale), and customer contact details submitted through forms (for example, email and name). Depending on
          how the merchant configures the app, it may also process event data (such as orders or subscription events)
          that are explicitly connected to notifications.
        </p>
        <p>
          This data is used only to power features like collecting subscribers, sending triggered notifications, and
          showing basic delivery history in the app. Wave Notify does not sell data and only shares data with required
          infrastructure providers (for example, Shopify, email/SMS providers configured by the merchant, and
          hosting/logging providers) to deliver the service.
        </p>
        <p>
          Data related to Wave Notify is retained as long as the merchant&apos;s shop uses the app or as needed for
          legal and security reasons. Data is removed or anonymized when the app is uninstalled or when the merchant
          requests deletion, subject to Shopify&apos;s data retention rules and applicable law.
        </p>

        {/* Wave Sync */}
        <h3 style={{ fontSize: '1.5rem', margin: '1.5rem 0 0.75rem' }}>Wave Sync – Privacy Overview</h3>
        <p>
          <strong>Wave Sync</strong> is a product syncing app that runs inside the merchant&apos;s Shopify admin. It
          reads product data via Shopify&apos;s Admin API, including product IDs, titles, handles, status, images,
          variants, inventory quantities, and prices, as well as basic shop information (such as shop domain, plan, and
          install date).
        </p>
        <p>
          Wave Sync also stores a minimal local copy of sync rules and product references (for example, product IDs,
          variant IDs, titles, and current inventory/price snapshots) in its own database so it can run syncs, logs, and
          analytics. This data is used only to keep products in sync (inventory, price, status), process webhooks, run
          safe background auto-sync, and show sync history and analytics inside the app.
        </p>
        <p>
          Wave Sync does not sell data and only shares it with essential infrastructure providers (for example,
          Shopify, hosting/database providers, logging/monitoring tools, and our support ticket portal if the merchant
          files a support request). Data is retained as long as the merchant&apos;s shop uses the app
          or as required for legal and security purposes, and is removed or anonymized after uninstall or upon request,
          in line with Shopify&apos;s data, GDPR, and other applicable requirements.
        </p>
      </section>

      {/* How We Use Your Information */}
      <section style={{ marginBottom: '2rem' }}>
        <h2 style={{ fontSize: '2rem', marginBottom: '1rem' }}>How We Use Your Information</h2>
        <p>
          We use the information we collect in various ways, including to:
        </p>
        <ul style={{ marginLeft: '1.5rem', marginBottom: '1rem' }}>
          <li>
            Provide, operate, and maintain our Site and services.
          </li>
          <li>
            Improve, personalize, and expand our Site and services.
          </li>
          <li>
            Understand and analyze how you use our Site, which helps us develop new products, services, features,
            and functionality.
          </li>
          <li>
            Process transactions and send you related information, such as confirmations and invoices.
          </li>
          <li>
            Send periodic emails regarding updates, news, and other information that may be of interest to you.
          </li>
          <li>
            Prevent fraudulent transactions, monitor against abuse, and ensure compliance with our policies.
          </li>
        </ul>
      </section>

      {/* Data Sharing & Disclosure */}
      <section style={{ marginBottom: '2rem' }}>
        <h2 style={{ fontSize: '2rem', marginBottom: '1rem' }}>Data Sharing & Disclosure</h2>
        <p>
          We do not sell or rent your personal information. However, we may share your data with trusted third parties
          in the following circumstances:
        </p>
        <ul style={{ marginLeft: '1.5rem', marginBottom: '1rem' }}>
          <li>
            <strong>Service Providers:</strong> Vendors and service providers who help us operate our Site and services,
            such as hosting providers, payment processors, and analytics companies. These parties are bound by
            confidentiality obligations.
          </li>
          <li>
            <strong>Legal Requirements:</strong> If required by law or in response to a legal request, we may disclose your
            information to government authorities or law enforcement agencies.
          </li>
          <li>
            <strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets, your data may be
            transferred as part of that transaction. In such cases, we will ensure the confidentiality of your data.
          </li>
          <li>
            <strong>Protection of Rights:</strong> We may share your information to protect the rights, property, or safety
            of WaveTech Solutions, our users, or others.
          </li>
        </ul>
      </section>

      {/* Data Security */}
      <section style={{ marginBottom: '2rem' }}>
        <h2 style={{ fontSize: '2rem', marginBottom: '1rem' }}>Data Security</h2>
        <p>
          The security of your data is important to us. We implement a variety of security measures to safeguard
          your personal information against unauthorized access, disclosure, alteration, or destruction.
        </p>
        <p>
          Although we strive to use commercially acceptable means to protect your personal information, no method
          of transmission over the Internet, or method of electronic storage, is 100% secure. Therefore, we
          cannot guarantee its absolute security.
        </p>
      </section>

      {/* Cookies & Tracking Technologies */}
      <section style={{ marginBottom: '2rem' }}>
        <h2 style={{ fontSize: '2rem', marginBottom: '1rem' }}>Cookies & Tracking Technologies</h2>
        <p>
          We use cookies and similar tracking technologies to track activity on our Site and hold certain information.
          Cookies are small files placed on your device that allow us to remember your preferences and provide a
          more personalized experience.
        </p>
        <p>
          You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent.
          However, if you do not accept cookies, some parts of our Site may not function properly.
        </p>
      </section>

      {/* Your Rights */}
      <section style={{ marginBottom: '2rem' }}>
        <h2 style={{ fontSize: '2rem', marginBottom: '1rem' }}>Your Rights</h2>
        <p>
          Depending on your jurisdiction, you may have certain rights regarding your personal information.
          These rights may include:
        </p>
        <ul style={{ marginLeft: '1.5rem', marginBottom: '1rem' }}>
          <li>The right to access and obtain a copy of your personal information.</li>
          <li>The right to correct any inaccuracies in your personal information.</li>
          <li>The right to request the deletion of your personal information.</li>
          <li>The right to object to or restrict the processing of your personal information.</li>
          <li>The right to data portability in some cases.</li>
        </ul>
        <p>
          If you wish to exercise any of these rights, please contact us using the information provided in the
          "Contact Information" section below.
        </p>
      </section>

      {/* Data Retention */}
      <section style={{ marginBottom: '2rem' }}>
        <h2 style={{ fontSize: '2rem', marginBottom: '1rem' }}>Data Retention</h2>
        <p>
          We will retain your personal information for as long as necessary to fulfill the purposes for which
          it was collected, as outlined in this Privacy Policy, unless a longer retention period is required or
          permitted by law.
        </p>
        <p>
          Once your data is no longer required, we will securely delete or anonymize it in accordance with
          our internal policies.
        </p>
      </section>

      {/* Policy Updates */}
      <section style={{ marginBottom: '2rem' }}>
        <h2 style={{ fontSize: '2rem', marginBottom: '1rem' }}>Policy Updates</h2>
        <p>
          We may update this Privacy Policy from time to time. When we do, we will revise the "Effective Date" at the
          top of this document and post the updated policy on our Site.
        </p>
        <p>
          We encourage you to review this Privacy Policy periodically to stay informed about how we are protecting
          your information.
        </p>
      </section>

      {/* Contact Information */}
      <section style={{ marginBottom: '2rem' }}>
        <h2 style={{ fontSize: '2rem', marginBottom: '1rem' }}>Contact Information</h2>
        <p>
          If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please
          contact us:
        </p>
        <ul style={{ marginLeft: '1.5rem', marginBottom: '1rem' }}>
          <li><strong>Email:</strong> daniel@wavetech.solutions</li>
        </ul>
        <p>
          We will make every effort to address your concerns promptly and in accordance with applicable laws.
        </p>
      </section>

      {/* Additional Information */}
      <section style={{ marginBottom: '2rem' }}>
        <h2 style={{ fontSize: '2rem', marginBottom: '1rem' }}>Additional Information</h2>
        <p>
          This Privacy Policy applies solely to information collected by WaveTech Solutions through our Site
          and services. It does not apply to any third-party websites or services that may be linked to or
          accessible from our Site.
        </p>
        <p>
          In cases where we rely on third-party services, we encourage you to review their privacy policies as
          well to understand how your information may be handled by those entities.
        </p>
        <p>
          Your continued use of our services after the posting of any changes to this Privacy Policy will be
          deemed acceptance of those changes.
        </p>
      </section>

      {/* Legal Disclaimer */}
      <section style={{ marginBottom: '2rem' }}>
        <h2 style={{ fontSize: '2rem', marginBottom: '1rem' }}>Legal Disclaimer</h2>
        <p>
          The information contained in this Privacy Policy is provided for informational purposes only. WaveTech Solutions
          makes no representations or warranties of any kind, express or implied, regarding the completeness or accuracy
          of this information. We shall not be liable for any errors or omissions in this policy or for any actions taken
          based on the information provided herein.
        </p>
        <p>
          Please consult with a legal professional if you have any questions regarding this Privacy Policy or your rights.
        </p>
      </section>

      {/* Final Note */}
      <section style={{ marginBottom: '2rem' }}>
        <h2 style={{ fontSize: '2rem', marginBottom: '1rem' }}>Final Note</h2>
        <p>
          At WaveTech Solutions, your privacy and security are our top priorities. We are committed to maintaining
          the confidentiality of your information and ensuring that your data is protected at all times.
        </p>
        <p>
          This Privacy Policy is subject to change without notice, so please check back regularly for updates.
        </p>
      </section>

      {/* End of Policy */}
      <section style={{ marginBottom: '2rem' }}>
        <p style={{ textAlign: 'center', fontSize: '1rem', opacity: 0.8 }}>
          © {new Date().getFullYear()} WaveTech Solutions. All rights reserved.
        </p>
      </section>
    </motion.div>
  );
}

export default PrivacyPolicy;